import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    defaultDropAnimationSideEffects,
    DragStartEvent,
    DragEndEvent,
    useDraggable,
    useDroppable,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Typography, IconButton, Paper, TextField, Select, MenuItem, FormControl, InputLabel, Divider, CircularProgress, Avatar, Tooltip, Fade } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { AccountTree as AccountTreeIcon, Tab as TabIcon } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MediaLibrary from '../common/MediaLibrary';
import VideoSettings, { VideoContent } from './common/VideoSettings';
import CodeTester from './CodeTester';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { FormControlLabel, Radio, RadioGroup, Switch } from '@mui/material';
import MermaidDiagram from '../common/MermaidDiagram';

// --- Types ---
export type BlockType = 'text' | 'heading' | 'list' | 'info' | 'warning' | 'danger' | 'success' | 'definition' | 'code' | 'image' | 'video' | 'tabs' | 'table' | 'html' | 'codeTester' | 'mermaid';

export interface Block {
    id: string;
    type: BlockType;
    content: any;
}

interface BlockEditorProps {
    initialBlocks?: Block[];
    onSave: (blocks: Block[]) => void;
    onCancel: () => void;
}

// --- Constants ---
const QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline', 'link'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }]
    ]
};

const createNewBlock = (type: BlockType): Block => ({
    id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    content: type === 'tabs' ? [{ title: 'Tab 1', content: '' }] :
        type === 'image' ? { url: '', alt: '', description: '', sourceType: 'url' } :
            type === 'video' ? { url: '', alt: '', description: '', sourceType: 'url', player: 'youtube' } :
                type === 'code' ? { code: '', language: 'javascript' } :
                    type === 'heading' ? { text: '', level: 2 } :
                        type === 'codeTester' ? { baseCode: '<h1>Hello World</h1>', language: 'html' } :
                            type === 'mermaid' ? 'graph TD;\nA[Début] --> B{Question?};\nB -- Oui --> C[Action];\nB -- Non --> D[Autre action];' : '',
});

const SidebarDraggableItem = ({ type, label, icon: IconComponent, onClick }: { type: BlockType, label: string, icon: React.ReactNode, onClick: () => void }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `sidebar-${type}`,
        data: {
            type: 'sidebar-item',
            blockType: type
        }
    });

    const sidebarButtonStyle = {
        justifyContent: 'flex-start',
        color: '#4a5568',
        borderColor: 'rgba(0,0,0,0.05)',
        bgcolor: 'white',
        borderRadius: 2,
        mb: 1,
        textTransform: 'none',
        fontWeight: 600,
        fontFamily: 'Quicksand, sans-serif',
        fontSize: '0.85rem',
        transition: 'all 0.2s',
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': {
            borderColor: '#667eea',
            bgcolor: 'rgba(102, 126, 234, 0.05)',
            transform: 'translateX(4px)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.1)'
        }
    };

    return (
        <Button
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            variant="outlined"
            startIcon={IconComponent}
            fullWidth
            sx={sidebarButtonStyle}
            onClick={onClick}
        >
            {label}
        </Button>
    );
};

// --- Sortable Block Component ---
const SortableBlock = React.memo(({ block, index, onRemove, onUpdate, setMediaLibraryOpen }: { block: Block, index: number, onRemove: (id: string) => void, onUpdate: (id: string, content: any) => void, setMediaLibraryOpen: (state: any) => void }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: block.id });
    const { showToast } = useToast();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '16px',
    };

    const renderBlockContent = () => {
        switch (block.type) {
            case 'text':
                return (
                    <ReactQuill
                        theme="snow"
                        value={block.content || ''}
                        onChange={(val) => onUpdate(block.id, val)}
                        modules={QUILL_MODULES}
                    />
                );
            case 'heading':
                const headingContent = typeof block.content === 'object' ? block.content : { text: block.content || '', level: 2 };
                return (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Select
                            value={headingContent.level || 2}
                            onChange={(e) => onUpdate(block.id, { ...headingContent, level: e.target.value })}
                            size="small"
                            sx={{ minWidth: 80 }}
                        >
                            {[1, 2, 3, 4, 5, 6].map(level => (
                                <MenuItem key={level} value={level}>H{level}</MenuItem>
                            ))}
                        </Select>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Heading Text"
                            value={headingContent.text || ''}
                            onChange={(e) => onUpdate(block.id, { ...headingContent, text: e.target.value })}
                        />
                    </Box>
                );
            case 'list':
                return (
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        variant="outlined"
                        placeholder="List items (use <ul><li> or just text)"
                        value={block.content || ''}
                        onChange={(e) => onUpdate(block.id, e.target.value)}
                        helperText="Type HTML list or plain text to be rendered as list"
                    />
                );
            case 'info':
            case 'warning':
            case 'danger':
            case 'success':
            case 'definition':
                const getBlockConfig = (type: string) => {
                    switch (type) {
                        case 'info':
                            return {
                                color: '#17a2b8',
                                bgcolor: 'rgba(23, 162, 184, 0.1)',
                                icon: <InfoIcon sx={{ color: '#17a2b8', mr: 1 }} />,
                                label: 'Information'
                            };
                        case 'warning':
                            return {
                                color: '#ffc107',
                                bgcolor: 'rgba(255, 193, 7, 0.1)',
                                icon: <WarningIcon sx={{ color: '#ffc107', mr: 1 }} />,
                                label: 'Warning'
                            };
                        case 'danger':
                            return {
                                color: '#dc3545',
                                bgcolor: 'rgba(220, 53, 69, 0.1)',
                                icon: <ErrorIcon sx={{ color: '#dc3545', mr: 1 }} />,
                                label: 'Attention'
                            };
                        case 'success':
                            return {
                                color: '#28a745',
                                bgcolor: 'rgba(40, 167, 69, 0.1)',
                                icon: <CheckCircleIcon sx={{ color: '#28a745', mr: 1 }} />,
                                label: 'Tip'
                            };
                        case 'definition':
                            return {
                                color: '#ff8c00',
                                bgcolor: 'rgba(255, 140, 0, 0.1)',
                                icon: <SchoolIcon sx={{ color: '#ff8c00', mr: 1 }} />,
                                label: 'Définition'
                            };
                        default:
                            return {
                                color: '#17a2b8',
                                bgcolor: 'rgba(23, 162, 184, 0.1)',
                                icon: <InfoIcon sx={{ color: '#17a2b8', mr: 1 }} />,
                                label: 'Note'
                            };
                    }
                };

                const config = getBlockConfig(block.type);

                return (
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: config.bgcolor,
                            borderLeft: `4px solid ${config.color}`,
                            borderRadius: '0 4px 4px 0',
                            mb: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {config.icon}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: config.color }}>
                                {config.label}
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            variant="standard"
                            placeholder={`Type your ${config.label.toLowerCase()} here...`}
                            value={block.content || ''}
                            onChange={(e) => onUpdate(block.id, e.target.value)}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.95rem', lineHeight: 1.6 } }}
                        />
                    </Box>
                );
            case 'code':
                const codeContent = typeof block.content === 'string'
                    ? { code: block.content, language: 'javascript' }
                    : block.content || { code: '', language: 'javascript' };

                return (
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: '#f5f5f5', px: 2, py: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>Code Block</Typography>
                            <Select
                                value={codeContent.language}
                                onChange={(e) => onUpdate(block.id, { ...codeContent, language: e.target.value })}
                                size="small"
                                variant="standard"
                                disableUnderline
                                sx={{ fontSize: '0.875rem', color: '#444', '& .MuiSelect-select': { py: 0 } }}
                            >
                                <MenuItem value="javascript">JavaScript</MenuItem>
                                <MenuItem value="typescript">TypeScript</MenuItem>
                                <MenuItem value="python">Python</MenuItem>
                                <MenuItem value="html">HTML</MenuItem>
                                <MenuItem value="css">CSS</MenuItem>
                                <MenuItem value="json">JSON</MenuItem>
                                <MenuItem value="sql">SQL</MenuItem>
                                <MenuItem value="bash">Bash</MenuItem>
                            </Select>
                        </Box>
                        <div className="codecontainer">
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                variant="standard"
                                placeholder="// Paste your code here"
                                value={codeContent.code}
                                onChange={(e) => onUpdate(block.id, { ...codeContent, code: e.target.value })}
                                InputProps={{ disableUnderline: true, style: { fontFamily: 'monospace', padding: '16px', fontSize: '0.9rem' } }}
                            />
                        </div>
                    </Box>
                );
            case 'image':
                const imgContent = typeof block.content === 'string' ? { url: block.content, alt: '', description: '', sourceType: 'url' } : block.content;

                const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files[0]) {
                        const formData = new FormData();
                        formData.append('file', e.target.files[0]);
                        try {
                            // Show loading state
                            onUpdate(block.id, { ...imgContent, url: 'uploading...', sourceType: 'upload' });

                            const res = await api.post('/api/media/upload', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                            });

                            console.log('Image upload response:', res.data);
                            if (res.data.url) {
                                onUpdate(block.id, { ...imgContent, url: res.data.url, sourceType: 'upload' });
                            } else {
                                showToast('Upload failed: No URL returned', 'error');
                                onUpdate(block.id, { ...imgContent, url: '', sourceType: 'upload' });
                            }
                        } catch (err: any) {
                            console.error("Upload failed", err);
                            showToast(`Upload failed: ${err.response?.data?.message || err.message}`, 'error');
                            onUpdate(block.id, { ...imgContent, url: '', sourceType: 'upload' });
                        }
                    }
                };

                return (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Image Settings</Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                value={imgContent.sourceType || 'url'}
                                onChange={(e) => onUpdate(block.id, { ...imgContent, sourceType: e.target.value })}
                            >
                                <FormControlLabel value="url" control={<Radio size="small" />} label="URL" />
                                <FormControlLabel value="upload" control={<Radio size="small" />} label="Upload" />
                                <FormControlLabel value="library" control={<Radio size="small" />} label="Library" />
                            </RadioGroup>
                        </FormControl>

                        {imgContent.sourceType === 'url' && (
                            <TextField
                                fullWidth
                                label="Image URL"
                                value={imgContent.url || ''}
                                onChange={(e) => onUpdate(block.id, { ...imgContent, url: e.target.value })}
                                size="small"
                                sx={{ mb: 2, mt: 1 }}
                            />
                        )}

                        {imgContent.sourceType === 'upload' && (
                            <Box sx={{ mb: 2, mt: 1 }}>
                                <Button variant="outlined" component="label" fullWidth>
                                    Upload Image
                                    <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                                </Button>
                                {imgContent.url && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Uploaded: {imgContent.url}</Typography>}
                            </Box>
                        )}

                        {imgContent.sourceType === 'library' && (
                            <Box sx={{ mb: 2, mt: 1 }}>
                                <Button variant="outlined" onClick={() => setMediaLibraryOpen({ open: true, blockId: block.id, type: 'image' })} fullWidth>
                                    Select from Library
                                </Button>
                                {imgContent.url && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Selected: {imgContent.url}</Typography>}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Alt Text"
                                value={imgContent.alt || ''}
                                onChange={(e) => onUpdate(block.id, { ...imgContent, alt: e.target.value })}
                                size="small"
                                helperText="For accessibility (screen readers)"
                            />
                            <TextField
                                fullWidth
                                label="Description"
                                value={imgContent.description || ''}
                                onChange={(e) => onUpdate(block.id, { ...imgContent, description: e.target.value })}
                                size="small"
                                helperText="Caption displayed below image"
                            />
                        </Box>


                        {imgContent.url && (
                            <Box sx={{ textAlign: 'center', p: 1, border: '1px dashed #ccc' }}>
                                {imgContent.url === 'uploading...' ? (
                                    <Box sx={{ p: 3 }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>Uploading image...</Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <img
                                            src={imgContent.url}
                                            alt={imgContent.alt}
                                            style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }}
                                            onError={(e: any) => {
                                                console.error('Image preview error:', imgContent.url);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        {imgContent.description && <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>{imgContent.description}</Typography>}
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            case 'video':
                const vidContent: VideoContent = typeof block.content === 'string'
                    ? { url: block.content, alt: '', description: '', sourceType: 'url', player: 'youtube' }
                    : block.content;

                return (
                    <VideoSettings
                        content={vidContent}
                        onUpdate={(newContent: VideoContent) => onUpdate(block.id, newContent)}
                        setMediaLibraryOpen={() => setMediaLibraryOpen({ open: true, blockId: block.id, type: 'video' })}
                    />
                );
            case 'tabs':
                const tabs = Array.isArray(block.content) ? block.content : [
                    { title: 'Windows', content: block.content?.windows || '' },
                    { title: 'macOS', content: block.content?.macos || '' },
                    { title: 'Linux', content: block.content?.linux || '' }
                ];

                const updateTab = (index: number, field: 'title' | 'content', value: string) => {
                    const newTabs = [...tabs];
                    newTabs[index] = { ...newTabs[index], [field]: value };
                    onUpdate(block.id, newTabs);
                };

                const addTab = () => {
                    onUpdate(block.id, [...tabs, { title: 'New Tab', content: '' }]);
                };

                const removeTab = (index: number) => {
                    const newTabs = tabs.filter((_: any, i: number) => i !== index);
                    onUpdate(block.id, newTabs);
                };

                return (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Tabs</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addTab}>Add Tab</Button>
                        </Box>

                        {tabs.map((tab: any, index: number) => (
                            <Box key={index} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField
                                        label="Tab Title"
                                        size="small"
                                        value={tab.title}
                                        onChange={(e) => updateTab(index, 'title', e.target.value)}
                                        sx={{ flexGrow: 1 }}
                                    />
                                    <IconButton size="small" color="error" onClick={() => removeTab(index)} disabled={tabs.length <= 1}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                                <TextField
                                    label="Content"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={tab.content}
                                    onChange={(e) => updateTab(index, 'content', e.target.value)}
                                />
                            </Box>
                        ))}
                    </Box>
                );
            case 'table':
                const tableData = {
                    headers: block.content?.headers || ['Header 1', 'Header 2'],
                    rows: block.content?.rows || [['Data 1', 'Data 2'], ['Data 3', 'Data 4']]
                };
                return (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Table</Typography>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
                            <thead>
                                <tr>
                                    {tableData.headers.map((header: string, i: number) => (
                                        <th key={i} style={{ border: '1px solid #ddd', padding: '8px', background: '#f5f5f5' }}>
                                            <input
                                                value={header}
                                                onChange={(e) => {
                                                    const newHeaders = [...tableData.headers];
                                                    newHeaders[i] = e.target.value;
                                                    onUpdate(block.id, { ...tableData, headers: newHeaders });
                                                }}
                                                style={{ width: '100%', border: 'none', background: 'transparent', fontWeight: 'bold' }}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.rows.map((row: string[], rowIndex: number) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell: string, cellIndex: number) => (
                                            <td key={cellIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                <input
                                                    value={cell}
                                                    onChange={(e) => {
                                                        const newRows = [...tableData.rows];
                                                        newRows[rowIndex][cellIndex] = e.target.value;
                                                        onUpdate(block.id, { ...tableData, rows: newRows });
                                                    }}
                                                    style={{ width: '100%', border: 'none' }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Typography variant="caption" color="text.secondary">Simple 2x2 table editing. Complex tables coming soon.</Typography>
                    </Box>
                );
            case 'html':
                return (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Raw HTML</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            variant="outlined"
                            placeholder="<div>Your HTML here</div>"
                            value={block.content || ''}
                            onChange={(e) => onUpdate(block.id, e.target.value)}
                            sx={{ mb: 2, fontFamily: 'monospace' }}
                        />
                        <Typography variant="caption" display="block" gutterBottom>Preview:</Typography>
                        <Box
                            sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#fff' }}
                            dangerouslySetInnerHTML={{ __html: block.content || '' }}
                        />
                    </Box>
                );
            case 'codeTester':
                const testerContent = block.content || { baseCode: '<h1>Hello World</h1>', language: 'html' };
                return (
                    <CodeTester
                        initialCode={testerContent.baseCode}
                        language={testerContent.language}
                        onCodeChange={(newCode) => onUpdate(block.id, { ...testerContent, baseCode: newCode })}
                        onLanguageChange={(newLang) => onUpdate(block.id, { ...testerContent, language: newLang })}
                        mode="builder"
                    />
                );
            case 'mermaid':
                return (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>Diagramme Mermaid</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            variant="outlined"
                            placeholder="graph TD;\nA-->B;"
                            value={block.content || ''}
                            onChange={(e) => onUpdate(block.id, e.target.value)}
                            sx={{ mb: 2, fontFamily: 'monospace' }}
                            helperText={
                                <span>
                                    Syntaxe Mermaid. <a href="https://mermaid.js.org/intro/" target="_blank" rel="noopener noreferrer">Documentation</a>
                                </span>
                            }
                        />
                        <Typography variant="caption" display="block" gutterBottom>Prévisualisation :</Typography>
                        <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1, bgcolor: '#fff', minHeight: '100px' }}>
                            <MermaidDiagram chart={block.content || ''} />
                        </Box>
                    </Box>
                );
            default:
                return <div>Unknown Block</div>;
        }
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={0}
            sx={{
                p: 0,
                position: 'relative',
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.05)',
                overflow: 'hidden',
                bgcolor: 'white',
                boxShadow: isDragging ? '0 15px 30px rgba(102, 126, 234, 0.15)' : '0 4px 12px rgba(0,0,0,0.03)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.1)',
                    borderColor: 'rgba(102, 126, 234, 0.2)',
                    '& .block-header': {
                        bgcolor: 'rgba(102, 126, 234, 0.05)',
                    }
                }
            }}
        >
            <Box
                className="block-header"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid rgba(0,0,0,0.03)',
                    bgcolor: 'rgba(0,0,0,0.01)',
                    transition: 'background-color 0.2s'
                }}
            >
                <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: '12px', color: '#a0aec0' }}>
                    <DragIndicatorIcon fontSize="small" />
                </div>
                <Avatar sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    mr: 1.5,
                    fontSize: '0.8rem'
                }}>
                    {block.type === 'text' ? <TextFieldsIcon fontSize="inherit" /> :
                        block.type === 'video' ? <VideoLibraryIcon fontSize="inherit" /> :
                            block.type === 'tabs' ? <TabIcon fontSize="inherit" /> :
                                block.type === 'code' ? <CodeIcon fontSize="inherit" /> :
                                    block.type === 'mermaid' ? <AccountTreeIcon fontSize="inherit" /> : <InfoIcon fontSize="inherit" />}
                </Avatar>
                <Typography variant="subtitle2" sx={{ flexGrow: 1, textTransform: 'capitalize', color: '#4a5568', fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                    Bloc {block.type}
                </Typography>
                <Tooltip title="Supprimer ce bloc">
                    <IconButton size="small" onClick={() => onRemove(block.id)} sx={{ color: '#f56565', '&:hover': { bgcolor: 'rgba(245, 101, 101, 0.1)' } }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ p: 3 }}>
                {renderBlockContent()}
            </Box>
        </Paper>
    );
});

SortableBlock.displayName = 'SortableBlock';

// --- Main Editor Component ---
const BlockEditor: React.FC<BlockEditorProps> = ({ initialBlocks = [], onSave, onCancel }) => {
    const [blocks, setBlocks] = useState<Block[]>(initialBlocks);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeSidebarItem, setActiveSidebarItem] = useState<{ id: string; type: BlockType; label: string; icon: React.ReactNode } | null>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { setNodeRef: setCanvasRef } = useDroppable({
        id: 'canvas-area',
    });

    // Debounced propagation to parent
    useEffect(() => {
        // Clear previous timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Set new timeout
        saveTimeoutRef.current = setTimeout(() => {
            onSave(blocks);
        }, 500); // 500ms delay

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [blocks, onSave]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Require 5px movement to start dragging, so clicks work
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as string);
        
        if (active.data.current?.type === 'sidebar-item') {
            const blockType = active.data.current.blockType;
            let label = 'Bloc';
            let icon = <InfoIcon />;
            
            // Infer basic label/icon for drag overlay
            if (blockType === 'text') { label = 'Texte riche'; icon = <TextFieldsIcon />; }
            else if (blockType === 'heading') { label = 'Titre'; icon = <Box sx={{ fontWeight: 'bold' }}>H</Box>; }
            else if (blockType === 'image') { label = 'Image'; icon = <ImageIcon />; }
            else if (blockType === 'video') { label = 'Vidéo'; icon = <VideoLibraryIcon />; }
            
            setActiveSidebarItem({ id: active.id as string, type: blockType, label, icon });
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.data.current?.type === 'sidebar-item') {
            const blockType = active.data.current.blockType;
            if (over) {
                const newBlock = createNewBlock(blockType);
                const overId = over.id;
                
                if (overId === 'canvas-area') {
                    // Dropped on empty space in canvas
                    setBlocks(prev => [...prev, newBlock]);
                } else {
                    // Dropped on an existing item in the list
                    const overIndex = blocks.findIndex(b => b.id === overId);
                    if (overIndex >= 0) {
                        setBlocks(prev => {
                            const newBlocks = [...prev];
                            newBlocks.splice(overIndex, 0, newBlock);
                            return newBlocks;
                        });
                    }
                }
            }
        } else {
            if (over && active.id !== over.id) {
                setBlocks((items) => {
                    const oldIndex = items.findIndex((item) => item.id === active.id);
                    const newIndex = items.findIndex((item) => item.id === over.id);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        }
        setActiveId(null);
        setActiveSidebarItem(null);
    };

    const addBlock = (type: BlockType) => {
        const newBlock = createNewBlock(type);
        setBlocks([...blocks, newBlock]);
    };

    const removeBlock = useCallback((id: string) => {
        setBlocks((prev) => prev.filter((b) => b.id !== id));
    }, []);

    const updateBlock = useCallback((id: string, content: any) => {
        setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, content } : b)));
    }, []);

    const [mediaLibraryOpen, setMediaLibraryOpen] = useState<{ open: boolean; blockId: string | null; type: 'image' | 'video' }>({ open: false, blockId: null, type: 'image' });

    const handleMediaSelect = (url: string) => {
        if (mediaLibraryOpen.blockId) {
            const block = blocks.find(b => b.id === mediaLibraryOpen.blockId);
            if (block) {
                const currentContent = typeof block.content === 'string' ? {} : block.content;
                updateBlock(mediaLibraryOpen.blockId, {
                    ...currentContent,
                    url: url,
                    sourceType: 'library',
                    player: mediaLibraryOpen.type === 'video' ? 'native' : undefined
                });
            }
        }
        setMediaLibraryOpen({ ...mediaLibraryOpen, open: false });
    };

    const sidebarButtonStyle = {
        justifyContent: 'flex-start',
        color: '#4a5568',
        borderColor: 'rgba(0,0,0,0.05)',
        bgcolor: 'white',
        borderRadius: 2,
        mb: 1,
        textTransform: 'none',
        fontWeight: 600,
        fontFamily: 'Quicksand, sans-serif',
        fontSize: '0.85rem',
        transition: 'all 0.2s',
        '&:hover': {
            borderColor: '#667eea',
            bgcolor: 'rgba(102, 126, 234, 0.05)',
            transform: 'translateX(4px)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.1)'
        }
    };

    const sectionTitleStyle = {
        mt: 2,
        mb: 1.5,
        color: '#718096',
        fontWeight: 800,
        textTransform: 'uppercase',
        fontSize: '0.65rem',
        letterSpacing: '1px',
        px: 1
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <Box sx={{ display: 'flex', height: '100%', bgcolor: '#f5f7fa' }}>
                {/* Sidebar / Toolbar */}
                <Box
                    sx={{
                        width: '260px',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: '1px solid rgba(0,0,0,0.05)',
                        bgcolor: 'white',
                        zIndex: 1,
                        boxShadow: '4px 0 15px rgba(0,0,0,0.02)'
                    }}
                >
                    <Typography variant="subtitle2" sx={{ ...sectionTitleStyle, mt: 0 }}>
                        Texte & Structure
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flexGrow: 1, pr: 0.5 }}>
                        <SidebarDraggableItem type="text" label="Texte riche" icon={<TextFieldsIcon sx={{ color: '#667eea' }} />} onClick={() => addBlock('text')} />
                        <SidebarDraggableItem type="heading" label="Titre (H1-H6)" icon={<Box sx={{ fontWeight: 'bold', color: '#667eea' }}>H</Box>} onClick={() => addBlock('heading')} />
                        <SidebarDraggableItem type="list" label="Liste à puces" icon={<Box sx={{ fontWeight: 'bold', color: '#667eea' }}>•</Box>} onClick={() => addBlock('list')} />

                        <Typography variant="subtitle2" sx={sectionTitleStyle}>Média & Visuels</Typography>
                        <SidebarDraggableItem type="image" label="Image" icon={<ImageIcon sx={{ color: '#10b981' }} />} onClick={() => addBlock('image')} />
                        <SidebarDraggableItem type="video" label="Vidéo" icon={<VideoLibraryIcon sx={{ color: '#10b981' }} />} onClick={() => addBlock('video')} />

                        <Typography variant="subtitle2" sx={sectionTitleStyle}>Alertes & Notes</Typography>
                        <SidebarDraggableItem type="info" label="Information" icon={<InfoIcon sx={{ color: '#17a2b8' }} />} onClick={() => addBlock('info')} />
                        <SidebarDraggableItem type="warning" label="Attention" icon={<WarningIcon sx={{ color: '#ffc107' }} />} onClick={() => addBlock('warning')} />
                        <SidebarDraggableItem type="danger" label="Danger / Erreur" icon={<ErrorIcon sx={{ color: '#dc3545' }} />} onClick={() => addBlock('danger')} />
                        <SidebarDraggableItem type="success" label="Astuce / Succès" icon={<CheckCircleIcon sx={{ color: '#28a745' }} />} onClick={() => addBlock('success')} />

                        <Typography variant="subtitle2" sx={sectionTitleStyle}>Interactif & Avancé</Typography>
                        <SidebarDraggableItem type="definition" label="Définition" icon={<SchoolIcon sx={{ color: '#ff8c00' }} />} onClick={() => addBlock('definition')} />
                        <SidebarDraggableItem type="code" label="Code source" icon={<CodeIcon sx={{ color: '#4584FF' }} />} onClick={() => addBlock('code')} />
                        <SidebarDraggableItem type="table" label="Tableau" icon={<Box sx={{ fontWeight: 'bold', fontSize: '14px', width: 20, textAlign: 'center', color: '#718096' }}>T</Box>} onClick={() => addBlock('table')} />
                        <SidebarDraggableItem type="tabs" label="Onglets" icon={<TabIcon sx={{ color: '#667eea' }} />} onClick={() => addBlock('tabs')} />
                        <SidebarDraggableItem type="html" label="Code HTML" icon={<CodeIcon sx={{ color: '#e34c26' }} />} onClick={() => addBlock('html')} />
                        <SidebarDraggableItem type="mermaid" label="Diagramme" icon={<AccountTreeIcon sx={{ color: '#ec407a' }} />} onClick={() => addBlock('mermaid')} />
                        <SidebarDraggableItem type="codeTester" label="Testeur de code" icon={<CodeIcon sx={{ color: '#9c27b0' }} />} onClick={() => addBlock('codeTester')} />
                    </Box>
                </Box>

                {/* Canvas Area */}
                <Box ref={setCanvasRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', maxWidth: '850px', minHeight: '100%' }}>
                        <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                            {blocks.map((block, index) => (
                                <Fade in key={block.id} timeout={400}>
                                    <Box>
                                        <SortableBlock
                                            block={block}
                                            index={index}
                                            onRemove={removeBlock}
                                            onUpdate={updateBlock}
                                            setMediaLibraryOpen={setMediaLibraryOpen}
                                        />
                                    </Box>
                                </Fade>
                            ))}
                        </SortableContext>

                        {blocks.length === 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '400px',
                                    color: '#a0aec0',
                                    border: '2px dashed rgba(102, 126, 234, 0.2)',
                                    borderRadius: 4,
                                    bgcolor: 'rgba(255,255,255,0.5)',
                                    backdropFilter: 'blur(10px)',
                                    p: 4,
                                    textAlign: 'center'
                                }}
                            >
                                <Box sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(102, 126, 234, 0.05)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 3
                                }}>
                                    <AddIcon sx={{ fontSize: 40, color: '#667eea' }} />
                                </Box>
                                <Typography variant="h5" sx={{ color: '#2d3748', fontWeight: 800, fontFamily: 'Quicksand, sans-serif', mb: 1 }}>
                                    Votre canevas est vide
                                </Typography>
                                <Typography variant="body1" sx={{ maxWidth: 400 }}>
                                    Cliquez sur un type de bloc dans la barre latérale ou glissez-le ici pour commencer à construire votre leçon.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <DragOverlay>
                {activeSidebarItem ? (
                    <Button
                        variant="outlined"
                        startIcon={activeSidebarItem.icon}
                        sx={{
                            justifyContent: 'flex-start',
                            color: '#4a5568',
                            borderColor: '#667eea',
                            bgcolor: 'white',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontFamily: 'Quicksand, sans-serif',
                            fontSize: '0.85rem',
                            width: '240px',
                            boxShadow: '0 10px 20px rgba(102, 126, 234, 0.2)'
                        }}
                    >
                        {activeSidebarItem.label}
                    </Button>
                ) : activeId && blocks.find(b => b.id === activeId) ? (
                    <Paper sx={{
                        p: 2,
                        opacity: 0.8,
                        width: '100%',
                        borderRadius: 3,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        border: '1px solid #667eea',
                        bgcolor: 'white'
                    }}>
                        <Typography sx={{ fontWeight: 700, color: '#667eea' }}>Déplacement du bloc...</Typography>
                    </Paper>
                ) : null}
            </DragOverlay>

            <MediaLibrary
                open={mediaLibraryOpen.open}
                onClose={() => setMediaLibraryOpen({ ...mediaLibraryOpen, open: false })}
                onSelect={handleMediaSelect}
            />
        </DndContext>
    );
};

export default BlockEditor;
